import { decodeUtf8Base64, encodeUtf8Base64 } from './base64';
import isValidBase64 from './isValidBase64';
import isValidImageURL from './urls/isValidImageURL';

const HANDLER_HOSTS: { [host: string]: string } = {
  'images-handler.kamereo.vn': 'production',
  'images-handler.qa.kamereo.vn': 'qa',
};

const S3_HOST = /^(?:(.+)\.)?s3-ap-southeast-1\.amazonaws\.com$/;

const BUCKET_ENV: { [bucket: string]: string } = {
  'image-handler-prod': 'production',
  'images.kamereo.vn': 'production',
  'image-handler-qa': 'qa',
  'images.qa.kamereo.vn': 'qa',
};

const HANDLER_ORIGIN: { [env: string]: string } = {
  production: 'https://images-handler.kamereo.vn',
  qa: 'https://images-handler.qa.kamereo.vn',
};

const DEFAULT_BUCKET: { [env: string]: string } = {
  production: 'image-handler-prod',
  qa: 'image-handler-qa',
};

const WEBP_QUALITY = 80;

const PIXEL_RATIO = 2;

const MAX_WIDTH = 1600;

const decodeKey = (segment: string): string => {
  try {
    return decodeURIComponent(segment).normalize('NFD');
  } catch (error) {
    return segment.normalize('NFD');
  }
};

const decodePayload = (path: string): { bucket: string; key: string } | undefined => {
  if (!isValidBase64(path)) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(decodeUtf8Base64(path));
    return parsed && parsed.bucket && parsed.key ? { bucket: parsed.bucket, key: parsed.key } : undefined;
  } catch (error) {
    return undefined;
  }
};

export const getSourceImage = (url: string): { bucket: string; key: string } | undefined => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (error) {
    return undefined;
  }

  if (!isValidImageURL(parsed.href)) {
    return undefined;
  }

  const path = parsed.pathname.slice(1);

  const handlerEnv = HANDLER_HOSTS[parsed.hostname];
  if (handlerEnv) {
    return decodePayload(path) || { bucket: DEFAULT_BUCKET[handlerEnv], key: decodeKey(path) };
  }

  const host = S3_HOST.exec(parsed.hostname);
  if (!host) {
    return undefined;
  }
  if (host[1]) {
    return { bucket: host[1], key: decodeKey(path) };
  }
  const separator = path.indexOf('/');
  if (separator < 1) {
    return undefined;
  }
  return { bucket: decodeKey(path.slice(0, separator)), key: decodeKey(path.slice(separator + 1)) };
};

const generateThumbnailImage = (url: string | null | undefined, displayWidth: number): string => {
  if (!url || !displayWidth || displayWidth <= 0) {
    return url || '';
  }
  const source = getSourceImage(url);
  if (!source) {
    return url;
  }
  const env = BUCKET_ENV[source.bucket];
  if (!env) {
    return url;
  }

  const payload = {
    bucket: source.bucket,
    key: source.key,
    edits: {
      resize: { width: Math.min(Math.round(displayWidth * PIXEL_RATIO), MAX_WIDTH) },
      toFormat: 'webp',
      webp: { quality: WEBP_QUALITY },
    },
  };

  return `${HANDLER_ORIGIN[env]}/${encodeUtf8Base64(JSON.stringify(payload))}`;
};

export default generateThumbnailImage;
